import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import { memo } from "react";

/**
 * Renders a nurse's tenure as whole years once they pass the one-year mark, otherwise whole months.
 * A nurse with no recorded start date renders an em-less dash rather than an empty cell, so the column
 * reads as "unknown" instead of "zero".
 *
 * Promoted from `NursesMatrixGrid/columns.tsx` so the desktop grid and the minimized approval popup
 * render seniority identically. Kept prop-driven and i18n-injected (`t` passed in) so it stays a pure,
 * shareable UIToolkit cell with no data-fetching of its own.
 */
export const SeniorityCell = memo(function SeniorityCell({ nurseStartDate, t }: { nurseStartDate: DateTime | undefined; t: TFunction }) {
	if (!nurseStartDate) return "-";

	const today = DateTime.now();
	const years = Math.floor(today.diff(nurseStartDate, "years").years);
	if (years > 0) return t(years === 1 ? "nurses.matrix.seniority.year" : "nurses.matrix.seniority.years", { count: years });

	const months = Math.floor(today.diff(nurseStartDate, "months").months);
	return t(months === 1 ? "nurses.matrix.seniority.month" : "nurses.matrix.seniority.months", { count: months });
});
